import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Clock, Award, Heart, Dumbbell, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Jersey Girls Fitness",
  "url": "https://jerseygirlsclubs.com",
  "logo": "https://jerseygirlsclubs.com/images/Jersey-Girls-Logo-768x671.jpeg",
  "description": "Women-only fitness centers across New Jersey and Philadelphia. Premium equipment, free group strength training, and a supportive community.",
  "telephone": "(856) 382-0555",
  "areaServed": ["New Jersey", "Philadelphia, PA"],
  "knowsAbout": ["Women's fitness", "Group strength training", "Health clubs"]
};

const testimonials = [
  {
    text: "Jersey Girls has everything I need and more. The women-only environment is so supportive and I never have to wait for equipment. Best fitness decision I've made!",
    author: "Sarah M.",
    location: "Voorhees"
  },
  {
    text: "The community here is amazing. Everyone is supportive and the staff really cares about your progress. I've achieved goals I never thought possible.",
    author: "Maria T.",
    location: "Philadelphia"
  },
  {
    text: "24/7 access means I can work out whenever fits my schedule. The equipment is top-notch and the group strength training is free. Highly recommend!",
    author: "Jessica L.",
    location: "Gloucester"
  }
];

export default function AboutPage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Premium Variety",
      description: "Our locations offer a wide variety of quality equipment designed for your goals. Brand names you can trust, with multiples of popular machines so you never wait."
    },
    {
      icon: Award,
      title: "Quality Equipment",
      description: "We invest in the best equipment and maintain it rigorously so you can train safely and effectively."
    },
    {
      icon: Clock,
      title: "Convenient Hours",
      description: "With extended hours and multiple locations, fitting in your workout has never been easier."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <SEO
        title="Why Choose Jersey Girls"
        description="Jersey Girls is committed to providing women with a supportive, women-only fitness environment. Premium equipment, free group strength training, and convenient hours. Join us today!"
        url="/about-us"
        structuredData={ABOUT_SCHEMA}
      />
      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-6xl font-heading font-black text-white mb-6" data-testid="about-heading">
                WHY JERSEY GIRLS?
              </h1>
              <p className="text-gray-700 text-lg leading-relaxed mb-8">
                Jersey Girls offers women-only fitness centers across New Jersey and Philadelphia. We're a supportive community dedicated to helping you achieve your health and fitness goals with premium equipment, free group strength training, and a space designed for you.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/locations">
                  <Button className="bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider shadow-neon-pink" data-testid="about-find-location-btn">
                    <MapPin className="w-4 h-4 mr-2" />
                    Find a Location
                  </Button>
                </Link>
                <Link to="/memberships">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="about-memberships-btn">
                    View Memberships
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img 
                  src="/images/WhyUsBkgd.jpg"
                  alt="Jersey Girls women-only fitness club interior"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 md:px-8 bg-gray-100" data-testid="values-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
              Why Us?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              What sets us apart from other fitness centers
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card-light no-hover p-6 text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-lg bg-brand-pink/20 flex items-center justify-center">
                  <value.icon className="w-7 h-7 text-brand-pink" />
                </div>
                <h3 className="text-xl font-heading font-bold text-black mb-2">{value.title}</h3>
                <p className="text-black text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 md:px-8" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
            <Quote className="w-12 h-12 mx-auto text-brand-pink mb-6" />
            <blockquote className="text-xl md:text-2xl text-gray-700 mb-6 leading-relaxed">
              "{testimonials[currentTestimonial].text}"
            </blockquote>
            <div className="flex items-center justify-center gap-2">
              <div className="h-1 w-1 rounded-full bg-brand-pink"></div>
              <p className="text-gray-600 font-medium">{testimonials[currentTestimonial].author}</p>
              <div className="h-1 w-1 rounded-full bg-brand-pink"></div>
              <p className="text-gray-500">{testimonials[currentTestimonial].location}</p>
            </div>
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentTestimonial ? "w-8 bg-brand-pink" : "w-2 bg-gray-300"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 bg-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Dumbbell className="w-16 h-16 mx-auto text-brand-pink mb-6" />
            <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-6">
              READY TO JOIN JERSEY GIRLS?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Find your nearest location and start your fitness journey today.
            </p>
            <Link to="/locations">
              <Button 
                size="lg"
                className="h-14 px-10 text-lg bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider shadow-neon-pink hover:shadow-neon-pink-lg animate-pulse-glow"
                data-testid="about-cta-btn"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Find Your Location
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
